import React, { useEffect, useRef } from 'react';

const Chart = ({ 
  data = [], 
  title = 'Gráfica de Series de Tiempo',
  showDataZoom = true,
  height = '400px',
  smooth = true,
  showSymbols = false,
  areaStyle = true,
  lineColumns = [],
  upperLimitColumn = null,
  lowerLimitColumn = null,
  barColumns = [],
  chartType = 'line',
  showLabels = false,
  groupByQuarter = false, // Nueva prop para agrupar por cuatrimestres
}) => {
  const chartRef = useRef(null);
  const echartInstance = useRef(null);

  useEffect(() => {
    const loadECharts = async () => {
      if (!window.echarts) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/echarts/5.4.3/echarts.min.js';
        script.onload = () => initChart();
        document.head.appendChild(script);
      } else {
        initChart();
      }
    };

    const initChart = () => {
      if (chartRef.current && window.echarts) {
        echartInstance.current = window.echarts.init(chartRef.current);
        updateChart();
      }
    };

    loadECharts();

    return () => {
      if (echartInstance.current) {
        echartInstance.current.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (echartInstance.current) {
      updateChart();
    }
  }, [data, title, showDataZoom, smooth, showSymbols, areaStyle, lineColumns, upperLimitColumn, lowerLimitColumn, barColumns, chartType, groupByQuarter]);

  // Función para obtener el cuatrimestre de una fecha
  const getQuarter = (date) => {
    const month = date.getMonth() + 1; // getMonth() retorna 0-11
    const year = date.getFullYear();
    
    if (month <= 4) return `${year}-Q1`;
    else if (month <= 8) return `${year}-Q2`;
    else return `${year}-Q3`;
  };

  // Función para agrupar datos por cuatrimestre
  const groupDataByQuarter = (rawData, dateColumn) => {
    const quarterGroups = {};
    
    rawData.forEach(row => {
      const date = new Date(row[dateColumn]);
      const quarter = getQuarter(date);
      
      if (!quarterGroups[quarter]) {
        quarterGroups[quarter] = [];
      }
      quarterGroups[quarter].push(row);
    });

    // Convertir los grupos en datos agregados
    const aggregatedData = [];
    
    Object.keys(quarterGroups).sort().forEach(quarter => {
      const quarterData = quarterGroups[quarter];
      const aggregatedRow = { [dateColumn]: quarter };
      
      // Obtener todas las columnas numéricas
      const numericColumns = Object.keys(quarterData[0]).filter(key => 
        key !== dateColumn && !isNaN(parseFloat(quarterData[0][key]))
      );
      
      // Calcular el promedio para cada columna numérica
      numericColumns.forEach(column => {
        const values = quarterData.map(row => parseFloat(row[column]) || 0);
        const sum = values.reduce((acc, val) => acc + val, 0);
        aggregatedRow[column] = sum / values.length;
      });
      
      aggregatedData.push(aggregatedRow);
    });
    
    return aggregatedData;
  };

  const updateChart = () => {
    if (!echartInstance.current || !data || data.length === 0) return;

    // Procesar los datos para ECharts
    const processedData = processDataForECharts(data);
    
    const option = {
      backgroundColor: '#ffff',
      tooltip: {
        trigger: 'axis',
        position: function (pt) {
          return [pt[0], '10%'];
        },
        formatter: function(params) {
          let tooltipContent = '';
          if (groupByQuarter && params.length > 0) {
            tooltipContent = `<strong>${params[0].axisValue}</strong><br/>`;
          }
          params.forEach(param => {
            const value = groupByQuarter ? param.value : param.value[1];
            tooltipContent += `${param.seriesName}: ${Number(value).toFixed(2)}<br/>`;
          });
          return tooltipContent;
        }
      },
      title: {
        left: 'center',
        text: title,
        textStyle: {
          color: '#6C757D'
        }
      },
      label: {
        show: showLabels,
        position: 'top',
        color: '#002060',
        fontSize: 12,
        formatter: function(params) {
          return groupByQuarter ? Number(params.value).toFixed(2) : Number(params.value[1]).toFixed(2);
        }
      },
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: 'none'
          },
          restore: {},
          saveAsImage: {}
        }
      },
      legend: {
        top: 30,
        data: processedData.seriesNames,
        textStyle: {
          color: '#6C757D'
        }
      },
      xAxis: {
        type: groupByQuarter ? 'category' : 'time',
        boundaryGap: groupByQuarter ? true : false, // true para categorías (barras), false para tiempo (líneas)
        axisLine: { lineStyle: { color: '#6C757D' }},
        axisLabel: { 
          color: '#6C757D',
          rotate: groupByQuarter ? 45 : 0 // Rotar etiquetas para cuatrimestres
        },
        data: groupByQuarter ? processedData.categories : undefined
      },
      yAxis: {
        type: 'value',
        boundaryGap: [0, '100%'],
        axisLine: { lineStyle: { color: '#6C757D' }},
        axisLabel: { color: '#6C757D' }
      },
      dataZoom: showDataZoom ? [
        {
          type: 'inside',
          start: 0,
          end: 20
        },
        {
          start: 0,
          end: 20
        }
      ] : [],
      series: processedData.series
    };

    echartInstance.current.setOption(option, true);
  };

  const processDataForECharts = (rawData) => {
    if (!rawData || rawData.length === 0) {
      return { series: [], seriesNames: [] };
    }

    const safeLineColumns = Array.isArray(lineColumns) ? lineColumns : [lineColumns].filter(Boolean);
    const safeBarColumns = Array.isArray(barColumns) ? barColumns : [barColumns].filter(Boolean);

    const firstRow = rawData[0];
    const keys = Object.keys(firstRow);
    
    // Encontrar la columna de fecha
    const dateColumn = keys.find(key => 
      key.toLowerCase().includes('fecha') || 
      key.toLowerCase().includes('date') || 
      key.toLowerCase().includes('time') ||
      key.toLowerCase().includes('timestamp')
    ) || keys[0];

    // Agrupar por cuatrimestre si está habilitado
    const processedRawData = groupByQuarter ? groupDataByQuarter(rawData, dateColumn) : rawData;

    const series = [];
    const seriesNames = [];
    const usedColumns = new Set();

    // Función auxiliar para preparar datos según el tipo de agrupación
    const prepareData = (data, column) => {
      if (groupByQuarter) {
        return data.map(row => parseFloat(row[column]) || 0);
      } else {
        return data.map(row => [
          new Date(row[dateColumn]).getTime(),
          parseFloat(row[column]) || 0
        ]);
      }
    };

    // Procesar líneas principales
    safeLineColumns.forEach(lineColumn => {
      if (lineColumn && keys.includes(lineColumn)) {
        const mainLineData = prepareData(processedRawData, lineColumn);
        
        series.push({
          name: lineColumn,
          type: safeBarColumns.includes(lineColumn) ? 'bar' : 'line',
          smooth: smooth,
          symbol: showSymbols ? 'circle' : 'none',
          lineStyle: {
            color: '#002060',
            width: 2
          },
          itemStyle: {
            color: '#002060'
          },
          areaStyle: areaStyle ? {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [{
                offset: 0, color: 'rgba(0, 32, 96, 0.3)'
              }, {
                offset: 1, color: 'rgba(0, 32, 96, 0.1)'
              }]
            }
          } : null,
          data: groupByQuarter ? mainLineData : mainLineData,
          z: 2
        });
        seriesNames.push(lineColumn);
        usedColumns.add(lineColumn);
      }
    });

    // Procesar límite superior
    if (upperLimitColumn && keys.includes(upperLimitColumn)) {
      const upperLimitData = prepareData(processedRawData, upperLimitColumn);
      
      series.push({
        name: `${upperLimitColumn} (Límite Superior)`,
        type: safeBarColumns.includes(upperLimitColumn) ? 'bar' : chartType,
        smooth: smooth,
        symbol: 'none',
        lineStyle: {
          color: '#6C757D',
          width: 2,
          type: 'dashed'
        },
        itemStyle: {
          color: '#6C757D'
        },
        data: groupByQuarter ? upperLimitData : upperLimitData,
        z: 1
      });
      seriesNames.push(`${upperLimitColumn} (Límite Superior)`);
      usedColumns.add(upperLimitColumn);
    }

    // Procesar límite inferior
    if (lowerLimitColumn && keys.includes(lowerLimitColumn)) {
      const lowerLimitData = prepareData(processedRawData, lowerLimitColumn);
      
      series.push({
        name: `${lowerLimitColumn} (Límite Inferior)`,
        type: safeBarColumns.includes(lowerLimitColumn) ? 'bar' : chartType,
        smooth: smooth,
        symbol: 'none',
        lineStyle: {
          color: '#6C757D',
          width: 2,
          type: 'dashed'
        },
        itemStyle: {
          color: '#6C757D'
        },
        data: groupByQuarter ? lowerLimitData : lowerLimitData,
        z: 1
      });
      seriesNames.push(`${lowerLimitColumn} (Límite Inferior)`);
      usedColumns.add(lowerLimitColumn);
    }

    const dataColumns = keys.filter(key => key !== dateColumn && !usedColumns.has(key));

    dataColumns.forEach((column, index) => {
      const columnData = prepareData(processedRawData, column);
      
      series.push({
        name: column,
        type: safeBarColumns.includes(column) ? 'bar' : safeLineColumns.includes(column) ? 'line' : chartType,
        smooth: smooth,
        symbol: showSymbols ? 'circle' : 'none',
        lineStyle: {
          color: index === 0 ? '#002060' : index === 1 ? '#C00000' : getColor(index)
        },
        itemStyle: {
          color: index === 0 ? '#002060' : index === 1 ? '#C00000' : getColor(index)
        },
        areaStyle: areaStyle ? {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{
              offset: 0, color: index === 0 ? 'rgba(0, 32, 96, 0.3)' : index === 1 ? 'rgba(192, 0, 0, 0.3)' : getColor(index, 0.1)
            }, {
              offset: 1, color: index === 0 ? 'rgba(0, 32, 96, 0.1)' : index === 1 ? 'rgba(192, 0, 0, 0.1)' : getColor(index, 0.1)
            }]
          }
        } : null,
        data: groupByQuarter ? columnData : columnData
      });
      seriesNames.push(column);
    });

    // Obtener las categorías para el eje X cuando se agrupa por cuatrimestre
    const categories = groupByQuarter ? processedRawData.map(row => row[dateColumn]) : [];

    // Si estamos agrupando por cuatrimestre, necesitamos configurar las categorías del eje X
    if (groupByQuarter && processedRawData.length > 0) {
      const categories = processedRawData.map(row => row[dateColumn]);
      // Agregar las categorías a la opción del gráfico
      setTimeout(() => {
        if (echartInstance.current) {
          const currentOption = echartInstance.current.getOption();
          currentOption.xAxis[0].data = categories;
          echartInstance.current.setOption(currentOption);
        }
      }, 0);
    }

    return { series, seriesNames, categories };
  };

  const getColor = (index, alpha = 1) => {
    const baseColors = [
      `rgba(0, 32, 96, ${alpha})`,  // #002060
      `rgba(192, 0, 0, ${alpha})`, // #C00000
      `rgba(230, 108, 55, ${alpha})`,
      `rgba(230, 108, 55, ${alpha})`,
      `rgba(255, 99, 132, ${alpha})`,
      `rgba(153, 102, 255, ${alpha})`,
      `rgba(54, 162, 235, ${alpha})`,
      `rgba(255, 206, 86, ${alpha})`
    ];
    return baseColors[index % baseColors.length];
  };

  // Función para redimensionar el gráfico cuando cambie el tamaño del contenedor
  useEffect(() => {
    const handleResize = () => {
      if (echartInstance.current) {
        echartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="w-full">
      <div 
        ref={chartRef} 
        className="border rounded-lg"
        style={{ borderColor: '#6C757D', width: '100%', height: height }}
      />
    </div>
  );
};

export default Chart;